/**
 * ELO rating system for team matchmaking.
 *
 * Base ELO algorithm adapted for team-vs-team games.
 * New teams start at 1200.
 * K-factor determines how much ratings change per match.
 */

/** Default starting rating */
export const DEFAULT_RATING = 1200;

/** K-factor: higher = more volatile ratings. Start higher, reduce over time. */
const K_FACTOR = 32;

export interface TeamRating {
  rating: number;
  gamesPlayed: number;
  wins: number;
  losses: number;
  draws: number;
}

/** Create a fresh rating for a new team */
export function createRating(): TeamRating {
  return {
    rating: DEFAULT_RATING,
    gamesPlayed: 0,
    wins: 0,
    losses: 0,
    draws: 0,
  };
}

/** Expected score for team A against team B (0-1) */
export function expectedScore(ratingA: number, ratingB: number): number {
  return 1 / (1 + 10 ** ((ratingB - ratingA) / 400));
}

/** K-factor adjusted by games played (higher for new teams) */
function getK(gamesPlayed: number): number {
  if (gamesPlayed < 10) return K_FACTOR * 1.5;
  if (gamesPlayed < 30) return K_FACTOR;
  return K_FACTOR * 0.75;
}

export interface RatingResult {
  teamA: TeamRating;
  teamB: TeamRating;
  deltaA: number;
  deltaB: number;
}

/**
 * Calculate new ratings after a match.
 * @param ratingA - Team A's current rating
 * @param ratingB - Team B's current rating
 * @param scoreA - Team A's actual score (1 = win, 0.5 = draw, 0 = loss)
 */
export function calculateRatings(
  ratingA: TeamRating,
  ratingB: TeamRating,
  scoreA: number,
): RatingResult {
  const expectedA = expectedScore(ratingA.rating, ratingB.rating);
  const kA = getK(ratingA.gamesPlayed);
  const kB = getK(ratingB.gamesPlayed);

  const deltaA = Math.round(kA * (scoreA - expectedA));
  const deltaB = Math.round(kB * ((1 - scoreA) - (1 - expectedA)));

  const newA: TeamRating = {
    rating: ratingA.rating + deltaA,
    gamesPlayed: ratingA.gamesPlayed + 1,
    wins: ratingA.wins + (scoreA === 1 ? 1 : 0),
    losses: ratingA.losses + (scoreA === 0 ? 1 : 0),
    draws: ratingA.draws + (scoreA === 0.5 ? 1 : 0),
  };

  const newB: TeamRating = {
    rating: ratingB.rating + deltaB,
    gamesPlayed: ratingB.gamesPlayed + 1,
    wins: ratingB.wins + (scoreA === 0 ? 1 : 0),
    losses: ratingB.losses + (scoreA === 1 ? 1 : 0),
    draws: ratingB.draws + (scoreA === 0.5 ? 1 : 0),
  };

  return { teamA: newA, teamB: newB, deltaA, deltaB };
}

/** Get a display-friendly rating tier name */
export function getRatingTier(rating: number): { name: string; color: string } {
  if (rating >= 2000) return { name: "Legendary", color: "text-yellow-400" };
  if (rating >= 1800) return { name: "Diamond", color: "text-cyan-400" };
  if (rating >= 1600) return { name: "Platinum", color: "text-slate-300" };
  if (rating >= 1400) return { name: "Gold", color: "text-amber-400" };
  if (rating >= 1200) return { name: "Silver", color: "text-gray-300" };
  return { name: "Bronze", color: "text-orange-400" };
}